// LogoutButton — drop into the existing TopBar.
//
// Usage in TopBar.jsx:
//
//   import LogoutButton from '../components/layout/LogoutButton';
//   ...
//   <LogoutButton />
//
// The button reads the user from AuthContext, displays their initial + name,
// and on click calls auth.logout() which clears server-side cookies and
// resets context state. React Router's redirect to /login is handled by
// RouteGuard once `user` becomes null.

import { useState, useRef, useEffect } from 'react';
import { LogOut, User as UserIcon, ChevronDown, Loader2 } from 'lucide-react';
import { useAuth } from '../../lib/AuthContext';
import { roleLabel } from '../../lib/permissions';

export default function LogoutButton() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  if (!user) return null;

  const initial = (user.name || user.email || '?').trim().charAt(0).toUpperCase();

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-100 transition-colors"
      >
        <div className="w-8 h-8 rounded-full bg-[#162a52] text-white flex items-center justify-center text-sm font-semibold">
          {initial}
        </div>
        <div className="hidden md:flex flex-col items-start leading-tight">
          <span className="text-sm font-medium text-[#162a52]">
            {user.name || user.email}
          </span>
          <span className="text-xs text-slate-500">{roleLabel(user.role)}</span>
        </div>
        <ChevronDown className="w-4 h-4 text-slate-400" />
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
          <div className="px-3 py-2 border-b border-slate-100">
            <div className="text-sm font-medium text-[#162a52] truncate">
              {user.name || 'Unnamed'}
            </div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
          </div>
          <button
            onClick={handleLogout}
            disabled={busy}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogOut className="w-4 h-4" />
            )}
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
